<script setup lang="ts">
import { ROLE } from "core/interfaces";
import { useField, useForm, useIsFormDirty, useIsFormValid } from "vee-validate";
import * as yup from "yup";

import { ALERT_STATUS } from "~/common/interfaces/alert-status.enum";
import type { UpdateUserByIdDto } from "~/common/interfaces/dto/users/update-user-by-id.dto";
import { useAlertStore } from "~/stores/alert";
import { useModalStore } from "~/stores/modal";
import { useUsersStore } from "~/stores/users";

const { t } = useI18n();
const route = useRoute();
const alertStore = useAlertStore();
const usersStore = useUsersStore();
const { teamModalEdit } = useModalStore();
const { handleSubmit, errors } = useForm({
	initialValues: {
		roles: teamModalEdit.user.roles,
		isActive: teamModalEdit.user.isActive
	},
	validationSchema: yup.object({
		roles: yup.array()
			.required(t("INPUT.roles.error.required"))
			.min(1, t("INPUT.roles.error.min")),
		isActive: yup.boolean()
	})
});
const isFormDirty = useIsFormDirty();
const isFormValid = useIsFormValid();

const onSubmit = handleSubmit(async (dto: UpdateUserByIdDto) => {
	usersStore.currentUser = await usersStore.patchById(teamModalEdit.user.id, dto);

	teamModalEdit.closeModal();

	if (route.name === "settings-team") {
		usersStore.getAll({}, usersStore.recentGetAllQuery);
	}

	alertStore.addAlert({
		title: t("COMPONENTS.TEAM.MODAL_EDIT.alert.title"),
		description: t("COMPONENTS.TEAM.MODAL_EDIT.alert.description", { email: teamModalEdit.user.email }),
		status: ALERT_STATUS.SUCCESS
	});
});

const { value: roles } = useField<ROLE[]>("roles");
const { value: isActive } = useField<boolean>("isActive");
</script>

<template>
	<AppModal
		:title="$t('COMPONENTS.TEAM.MODAL_EDIT.title')"
		:close-button-text="$t('COMMON.cancel')"
		:submit-button-text="$t('COMMON.save')"
		:submit-button-active="isFormValid && isFormDirty"
		@close="teamModalEdit.closeModal"
		@submit="onSubmit"
	>
		<span class="font-semibold text-base">
			{{ $t('COMMON.generalInfo') }}
		</span>
		<div class="mt-4 flex flex-col gap-y-2">
			<AppCheckbox v-model="isActive">
				{{ $t('COMMON.active') }}
			</AppCheckbox>
			<AppInputTag
				v-model="roles"
				:title="$t('INPUT.roles.title')"
				:placeholder="Object.values(ROLE).join(', ')"
				:white-list="Object.values(ROLE)"
				:error="errors.roles"
				required
			/>
		</div>
	</AppModal>
</template>
